import { SaveChangesConfirmation } from '@components/SaveChangesConfirmation';
import { useToast } from '@contexts/Toast';
import { Axios, getErrorMessage } from '@helpers/ApiService';
import {
  errorMessage,
  fileUploadConfig,
  responseTexts,
} from '@helpers/Constants';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import { ValidationService } from '@services/ValidationService';
import { Address, AddressData } from '@helpers/Models';
import { useEffect, useRef, useState } from 'react';
import { useInputValidation } from '@hooks/useInputValidation';
import { useDismissDropdown } from '@services/DropdownService';
import { Assets } from '@helpers/Constants/Assets';
import { CustomerOrderService } from '@services/CustomerService';
import { AxiosError } from 'axios';

type ConfirmationType = 'admin' | 'password';

export const StoreDetails: React.FC = () => {
  const [isStoreDetailsEditing, setIsStoreDetailsEditing] = useState(false);
  const [isPasswordEditing, setIsPasswordEditing] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isBillingSameAsStore, setIsBillingSameAsStore] = useState(true);
  const [, setConfirmationType] = useState<ConfirmationType | null>(null);
  const { showToastMessage } = useToast();

  // Store details state
  const [storeDetails, setStoreDetails] = useState({
    companyName: '',
    contactName: '',
    storeEmail: '',
    storePhone: '',
    phoneNumber: '',
  });

  // Phone validation state
  const [phoneError, setPhoneError] = useState('');

  // Custom handler for phone number input with validation
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Only allow digits and limit to 10 characters
    if (/^\d*$/.test(inputValue) && inputValue.length <= 10) {
      setStoreDetails((prev) => ({
        ...prev,
        phoneNumber: inputValue,
      }));

      // Validate phone number
      if (inputValue.length > 0 && inputValue.length !== 10) {
        setPhoneError('Phone number must be exactly 10 digits');
      } else {
        setPhoneError('');
      }
    }
  };

  // Handle key press to only allow digits
  const handlePhoneKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      !/\d/.test(e.key) &&
      !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(e.key)
    ) {
      e.preventDefault();
    }
  };

  // Address state - using the same structure as ProfileAddress
  const initialAddress: Address = {
    storeName: '',
    contactName: '',
    address1: '',
    address2: '',
    address3: '',
    city: { id: 0, cityName: '' },
    state: { id: 0, stateName: '' },
    postalCode: { id: 0, postalCode: '' },
  };

  const [storeAddress, setStoreAddress] = useState<Address>(initialAddress);

  // Billing address state
  const [billingAddress, setBillingAddress] = useState<Address>(initialAddress);

  // Backup states for cancel functionality
  const [backupStoreDetails, setBackupStoreDetails] = useState(storeDetails);
  const [backupStoreAddress, setBackupStoreAddress] =
    useState<Address>(initialAddress);
  const [backupBillingAddress, setBackupBillingAddress] =
    useState<Address>(initialAddress);
  const [backupPhoneError, setBackupPhoneError] = useState('');

  // ZIP code validation hooks
  const storeZipValidation = useInputValidation(
    'zipCode',
    storeAddress.postalCode.postalCode,
  );
  const billingZipValidation = useInputValidation(
    'zipCode',
    billingAddress.postalCode.postalCode,
  );

  // Sync validation hooks with address state when data is loaded
  useEffect(() => {
    storeZipValidation.setValue(storeAddress.postalCode.postalCode);
  }, [storeAddress.postalCode.postalCode, storeZipValidation]);

  useEffect(() => {
    billingZipValidation.setValue(billingAddress.postalCode.postalCode);
  }, [billingAddress.postalCode.postalCode, billingZipValidation]);

  const [storeLogo, setStoreLogo] = useState<string | undefined>(undefined);
  const [activeDropdown, setActiveDropdown] = useState<boolean>();
  const dropdownRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Password state
  const [changePasswordObject, setChangePasswordObject] = useState<{
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
  }>({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    fetchStoreDetails();
    fetchStoreLogo();
  }, []);

  const fetchStoreLogo = () => {
    const logo = JSON.parse(localStorage.getItem('butcherDetails') || 'null');
    if (logo) {
      setStoreLogo(logo.profileImage?.logoLocation || Assets.BUTCHER_HOUSE);
    }
  };

  /**
   * Fetches store details from the API and populates the form
   */
  const fetchStoreDetails = async () => {
    try {
      const response = await Axios.get(ApiEndpoints.FETCH_ADDRESS);
      const data = response.data.data;
      const addresses = data.addresses;
      const userDetails = data.userDetails;

      // Set store details
      setStoreDetails({
        companyName: addresses?.companyName || '',
        contactName: userDetails?.name || '',
        storeEmail: userDetails?.email || '',
        storePhone: userDetails?.phone || '',
        phoneNumber: userDetails?.phone || '',
      });

      // Set store address - handle array structure
      if (addresses?.storeAddress && addresses.storeAddress.length > 0) {
        const storeAddr = addresses.storeAddress[0];
        console.log(storeAddr);

        //call the postalcode API with storeAddr.postalCodeId
        if (storeAddr.postalCodeId) {
          const postalCodeResponse = await Axios.post(
            ApiEndpoints.GET_STATE_CITY_POSTAL,
            {
              postalCodeId: storeAddr.postalCodeId,
            },
          );

          console.log(postalCodeResponse);
          setStoreAddress({
            storeName: addresses?.companyName || '',
            contactName: userDetails?.name || addresses?.contactName || '',
            address1: storeAddr.address1 || '',
            address2: storeAddr.address2 || '',
            address3: storeAddr.address3 || '',
            city: postalCodeResponse.data.data.cities[0] || [],
            state: postalCodeResponse.data.data.states[0] || [],
            postalCode: postalCodeResponse.data.data.postalCodes[0] || [],
          });
        }
      }

      // Set billing address - handle array structure
      if (addresses?.billingAddress && addresses.billingAddress.length > 0) {
        const billingAddr = addresses.billingAddress[0];
        const postalCodeResponse = await Axios.post(
          ApiEndpoints.GET_STATE_CITY_POSTAL,
          {
            postalCodeId: billingAddr.postalCodeId,
          },
        );
        setBillingAddress({
          storeName: addresses?.companyName || '',
          contactName: addresses?.contactName || '',
          address1: billingAddr.address1 || '',
          address2: billingAddr.address2 || '',
          address3: billingAddr.address3 || '',
          city: postalCodeResponse.data.data.cities[0] || '',
          state: postalCodeResponse.data.data.states[0] || '',
          postalCode: postalCodeResponse.data.data.postalCodes[0] || '',
        });

        // Check if billing address is same as store address
        if (addresses.storeAddress?.length > 0) {
          const storeAddr = addresses.storeAddress[0];
          const isSame =
            storeAddr.address1 === billingAddr.address1 &&
            storeAddr.address2 === billingAddr.address2 &&
            storeAddr.address3 === billingAddr.address3 &&
            storeAddr.stateId === billingAddr.stateId &&
            storeAddr.cityId === billingAddr.cityId &&
            storeAddr.postalCodeId === billingAddr.postalCodeId;
          setIsBillingSameAsStore(isSame);
        }
      }
    } catch (error) {
      showToastMessage(getErrorMessage(error), 'error');
    }
  };

  /**
   * State controlling visibility of password fields.
   *
   * Each property represents whether the corresponding password
   * input field is visible (`true`) or masked (`false`).
   *
   * @state
   * @property {boolean} oldPassword - Visibility of the old password field.
   * @property {boolean} newPassword - Visibility of the new password field.
   * @property {boolean} confirmPassword - Visibility of the confirm password field.
   */
  const [showPasswords, setShowPasswords] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  /**
   * State for storing validation errors for password fields.
   *
   * Each property contains the error message for the corresponding
   * password field, or an empty string if the field is valid.
   *
   * @state
   * @property {string} oldPassword - Error message for old password field.
   * @property {string} newPassword - Error message for new password field.
   * @property {string} confirmPassword - Error message for confirm password field.
   */
  const [passwordErrors, setPasswordErrors] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  /**
   * Toggles store details editing mode.
   *
   * Switches the `isStoreDetailsEditing` state between true and false,
   * enabling or disabling the store details editing UI elements.
   *
   * @function toggleStoreDetailsEditing
   * @returns {void}
   */
  const toggleStoreDetailsEditing = (): void => {
    if (!isStoreDetailsEditing) {
      // Entering edit mode - backup current values
      setBackupStoreDetails({ ...storeDetails });
      setBackupStoreAddress({ ...storeAddress });
      setBackupBillingAddress({ ...billingAddress });
      setBackupPhoneError(phoneError);
    }
    setIsStoreDetailsEditing(!isStoreDetailsEditing);
  };

  /**
   * Exits store details editing mode.
   *
   * Sets the `isStoreDetailsEditing` state to false, hiding or disabling
   * the store details editing UI elements.
   * Restores original values that were backed up when edit mode was entered.
   *
   * @function closeStoreDetailsEditing
   * @returns {void}
   */
  const closeStoreDetailsEditing = (): void => {
    // Restore backed up values
    setStoreDetails({ ...backupStoreDetails });
    setStoreAddress({ ...backupStoreAddress });
    setBillingAddress({ ...backupBillingAddress });
    setPhoneError(backupPhoneError);

    // Also reset validation states to match restored values
    storeZipValidation.setValue(backupStoreAddress.postalCode.postalCode);
    billingZipValidation.setValue(backupBillingAddress.postalCode.postalCode);

    setIsStoreDetailsEditing(false);
  };

  /**
   * Toggles password editing mode.
   *
   * Switches the `isPasswordEditing` state between true and false,
   * enabling or disabling the password editing UI elements.
   *
   * @function togglePasswordEditing
   * @returns {void}
   */
  const togglePasswordEditing = (): void => {
    setIsPasswordEditing(!isPasswordEditing);
    setPasswordErrors({
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
  };

  /**
   * Exits password editing mode.
   *
   * Sets the `isPasswordEditing` state to false, hiding or disabling
   * the password editing UI elements.
   *
   * @function closePasswordEditing
   * @returns {void}
   */
  const closePasswordEditing = (): void => {
    setIsPasswordEditing(false);
    setChangePasswordObject({
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
  };

  /**
   * Toggles the visibility of a password input field.
   *
   * Inverts the visibility state of the specified password field
   * (`oldPassword`, `newPassword`, or `confirmPassword`) in the
   * `showPasswords` state object.
   *
   * @function togglePasswordVisibility
   * @param {'oldPassword' | 'newPassword' | 'confirmPassword'} field
   *        - The specific password field whose visibility should be toggled.
   * @returns {void}
   */
  const togglePasswordVisibility = (
    field: 'oldPassword' | 'newPassword' | 'confirmPassword',
  ): void => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  /**
   * Handles ZIP code lookup when user enters 5 digits
   */
  const handleZipCodeLookup = async (
    zipCode: string,
    addressType: 'store' | 'billing',
  ) => {
    if (zipCode.length === 5 && /^\d{5}$/.test(zipCode)) {
      try {
        console.log(
          `Looking up ZIP code: ${zipCode} for ${addressType} address`,
        );
        const response = await Axios.post(ApiEndpoints.GET_STATE_CITY_POSTAL, {
          postalCode: zipCode,
        });

        console.log('ZIP code lookup response:', response.data);

        if (response.data.status === 'success') {
          const addressData: AddressData = response.data.data;

          if (
            addressData.cities &&
            addressData.cities.length > 0 &&
            addressData.states &&
            addressData.states.length > 0 &&
            addressData.postalCodes &&
            addressData.postalCodes.length > 0
          ) {
            const city = addressData.cities[0];
            const state = addressData.states[0];
            const postalCode = addressData.postalCodes[0];

            console.log(city, state, postalCode);
            console.log(
              `Found city: ${city.cityName}, state: ${state.stateName}, postalCode: ${postalCode.postalCode} for ZIP: ${zipCode}`,
            );

            if (addressType === 'store') {
              setStoreAddress((prev) => ({
                ...prev,
                city: city,
                state: state,
                postalCode: postalCode,
              }));
              showToastMessage(
                `Address found: ${city.cityName}, ${state.stateName}`,
                'success',
              );
            } else {
              setBillingAddress((prev) => ({
                ...prev,
                city: city,
                state: state,
                postalCode: postalCode,
              }));
              showToastMessage(
                `Address found: ${city.cityName}, ${state.stateName}`,
                'success',
              );
            }
          } else {
            console.log(
              'No city/state/postalCode found for ZIP code:',
              zipCode,
            );
            showToastMessage('ZIP code not found', 'error');
          }
        } else {
          console.log('API response not successful:', response.data);
          showToastMessage('Failed to lookup ZIP code', 'error');
        }
      } catch (error) {
        if (error instanceof AxiosError) {
          showToastMessage(
            error.response?.data?.message || 'Error fetching address data',
            'error',
          );
        }
      }
    }
  };

  /**
   * Handles store address field changes with hierarchical loading
   */
  const handleStoreAddressChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setStoreAddress((prev) => {
      const updated = { ...prev };

      if (name === 'storeName') {
        // Map storeName input to storename in state
        updated.storeName = value;
      } else if (name === 'contactName') {
        // Map contactName input to contactname in state
        updated.contactName = value;
      } else if (
        name === 'address1' ||
        name === 'address2' ||
        name === 'address3'
      ) {
        // Handle text address fields

        updated[name] = value;
      } else if (name === 'postalCode') {
        // Handle postal code - validation is handled by the hook
        updated.postalCode = { ...prev.postalCode, postalCode: value };
        // ZIP code lookup when 5 valid digits are entered
        if (value.length === 5 && /^\d{5}$/.test(value)) {
          handleZipCodeLookup(value, 'store');
        }
      } else if (name === 'city') {
        // Handle city - update the city object
        updated.city = { ...prev.city, cityName: value };
      } else if (name === 'state') {
        // Handle state - update the state object
        updated.state = { ...prev.state, stateName: value };
      }

      return updated;
    });
  };

  /**
   * Handles billing address field changes with hierarchical loading
   */
  const handleBillingAddressChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setBillingAddress((prev) => {
      const updated = { ...prev };

      if (name === 'address1' || name === 'address2' || name === 'address3') {
        // Handle text address fields
        updated[name] = value;
      } else if (name === 'postalCode') {
        // Handle postal code - validation is handled by the hook
        updated.postalCode = { ...prev.postalCode, postalCode: value };
        // ZIP code lookup when 5 valid digits are entered
        if (value.length === 5 && /^\d{5}$/.test(value)) {
          handleZipCodeLookup(value, 'billing');
        }
      } else if (name === 'city') {
        // Handle city - update the city object
        updated.city = { ...prev.city, cityName: value };
      } else if (name === 'state') {
        // Handle state - update the state object
        updated.state = { ...prev.state, stateName: value };
      }

      return updated;
    });
  };

  // Custom ZIP code handlers that sync validation hooks with address state
  const handleStoreZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    storeZipValidation.handleChange(e);

    // Update store address state
    setStoreAddress((prev) => ({
      ...prev,
      postalCode: { ...prev.postalCode, postalCode: value },
    }));

    // ZIP code lookup when 5 valid digits are entered
    if (value.length === 5 && /^\d{5}$/.test(value)) {
      handleZipCodeLookup(value, 'store');
    }
  };

  const handleBillingZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    billingZipValidation.handleChange(e);

    // Update billing address state
    setBillingAddress((prev) => ({
      ...prev,
      postalCode: { ...prev.postalCode, postalCode: value },
    }));

    // ZIP code lookup when 5 valid digits are entered
    if (value.length === 5 && /^\d{5}$/.test(value)) {
      handleZipCodeLookup(value, 'billing');
    }
  };

  // /**
  //  * Fetches billing address data hierarchically
  //  */
  // const fetchBillingAddressData = async (
  //   stateId?: number,
  //   cityId?: number,
  // ): Promise<AddressData | null> => {
  //   try {
  //     setLoading(true);
  //     const requestBody: { stateId?: number; cityId?: number } = {};

  //     if (stateId) requestBody.stateId = stateId;
  //     if (cityId) requestBody.cityId = cityId;

  //     const response = await Axios.post(
  //       ApiEndpoints.GET_STATE_CITY_POSTAL,
  //       requestBody,
  //     );

  //     if (response.data.status === 'success') {
  //       const data: AddressData = response.data.data;

  //       if (stateId && !cityId) {
  //         // State selected - load cities for this state, clear postal codes
  //         setBillingCities(data.cities || []);
  //         setBillingPostalCodes([]);
  //       } else if (stateId && cityId) {
  //         // Both state and city selected - load postal codes for this combination
  //         setBillingPostalCodes(data.postalCodes || []);
  //       }

  //       return data;
  //     }
  //     return null;
  //   } catch (error) {
  //     console.error('Error fetching billing address data:', error);
  //     showToastMessage(
  //       'Error loading billing address data. Please try again.',
  //       'error',
  //     );
  //     return null;
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Effect to sync billing address when checkbox changes
  useEffect(() => {
    if (isBillingSameAsStore) {
      setBillingAddress(storeAddress);
    }
  }, [isBillingSameAsStore, storeAddress]);

  /**
   * Prepares address payload for API submission
   */
  const prepareAddressPayload = (address: Address) => ({
    address1: address.address1,
    address2: address.address2,
    address3: address.address3,
    stateId: address.state ? address.state.id : undefined,
    cityId: address.city ? address.city.id : undefined,
    postalCodeId: address.postalCode ? address.postalCode.id : undefined,
  });

  /**
   * Saves store details to the API
   */
  const saveStoreDetails = async () => {
    try {
      const payload = {
        storeAddress: prepareAddressPayload(storeAddress),
        billingAddress: isBillingSameAsStore
          ? prepareAddressPayload(storeAddress)
          : prepareAddressPayload(billingAddress),
        sameAsStore: isBillingSameAsStore,
        storeEmail: storeDetails.storeEmail,
        storePhone: storeDetails.storePhone,
        phoneNumber: storeDetails.phoneNumber,
      };

      await Axios.put(ApiEndpoints.UPDATE_STORE_DETAILS, payload);
      showToastMessage(responseTexts.ADDRESS_CREATE_SUCCESS, 'success');
      setIsStoreDetailsEditing(false);

      // Refresh data
      await fetchStoreDetails();
    } catch (error) {
      showToastMessage(getErrorMessage(error), 'error');
    }
  };

  /**
   * Validates password fields and updates error state.
   *
   * Uses ValidationService to validate each password field and updates
   * the passwordErrors state with any validation messages.
   *
   * @function validatePasswordFields
   * @returns {boolean} - Returns true if all fields are valid, false otherwise.
   */
  const validatePasswordFields = (): boolean => {
    const errors = {
      oldPassword:
        changePasswordObject.oldPassword.trim() === ''
          ? 'Current password is required'
          : '',
      newPassword: ValidationService.validateField(
        'newPassword',
        changePasswordObject.newPassword,
        changePasswordObject,
      ),
      confirmPassword: ValidationService.validateField(
        'confirmPassword',
        changePasswordObject.confirmPassword,
        changePasswordObject,
      ),
    };

    setPasswordErrors(errors);
    return Object.values(errors).every((error) => error === '');
  };

  /**
   * Handles password field changes and validates the field.
   *
   * Updates the changePasswordObject state and validates the changed field,
   * clearing any existing error for that field if it becomes valid.
   *
   * @function handlePasswordChange
   * @param {'oldPassword' | 'newPassword' | 'confirmPassword'} field - The field being changed.
   * @param {string} value - The new value for the field.
   * @returns {void}
   */
  const handlePasswordChange = (
    field: 'oldPassword' | 'newPassword' | 'confirmPassword',
    value: string,
  ): void => {
    setChangePasswordObject((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (passwordErrors[field]) {
      setPasswordErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  /**
   * Prepares and displays the confirmation prompt for a save action.
   *
   * For password changes, validates all password fields before showing
   * the confirmation dialog. Sets the type of confirmation to the provided
   * `type` and makes the confirmation dialog visible if validation passes.
   *
   * @function handleSave
   * @param {ConfirmationType} type - The type of confirmation to display
   *                                  (e.g., password change, data update).
   * @returns {void}
   */
  const handleSave = (type: ConfirmationType): void => {
    if (type === 'password') {
      if (!validatePasswordFields()) {
        return; // Don't show confirmation if validation fails
      }
    } else if (type === 'admin') {
      // For store details, save directly without confirmation
      saveStoreDetails();
      return;
    }

    setConfirmationType(type);
    setShowConfirmation(true);
  };

  /**
   * Submits a change password request to the API.
   *
   * Sends the `changePasswordObject` payload to the `CHANGE_PASSWORD` endpoint.
   * On success, hides the confirmation prompt and displays a success toast message.
   * On failure, displays an error toast message with the relevant error details.
   *
   * @async
   * @function handleSubmit
   * Resolves when the API request completes.
   * @returns {Promise<void>}
   */
  const handleSubmit: () => Promise<void> = async () => {
    try {
      await Axios.post(ApiEndpoints.CHANGE_PASSWORD, changePasswordObject);
      setShowConfirmation(false);
      showToastMessage(responseTexts.CHANGE_PASSWORD_SUCCESS, 'success');

      // Reset password form after successful update
      setChangePasswordObject({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      // Reset password visibility states
      setShowPasswords({
        oldPassword: false,
        newPassword: false,
        confirmPassword: false,
      });

      // Reset password errors
      setPasswordErrors({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      // Exit password editing mode
      setIsPasswordEditing(false);
    } catch (error) {
      setShowConfirmation(false);
      if (error instanceof AxiosError && error.response) {
        showToastMessage(error.response.data.errors[0].message, 'error');
      } else {
        showToastMessage(getErrorMessage(error), 'error');
      }
    }
  };

  /**
   * Handles the cancel action for a save operation.
   *
   * This function is triggered when the user decides not to proceed
   * with saving changes. It hides the confirmation prompt by setting
   * the `showConfirmation` state to `false`.
   *
   * @function cancelSave
   * Does not return a value.
   * @returns {void}
   */
  const cancelSave = (): void => {
    setShowConfirmation(false);
  };

  /**
   * useDismissDropdown
   *
   * A custom React hook that listens for clicks outside the provided element refs,
   * and triggers a callback when such an event occurs. Typically used to close dropdowns or popovers.
   *
   * @param {Object} props - The hook parameters.
   * @param {React.RefObject<HTMLElement>[]} props.refs - An array of refs to dropdown elements.
   *        The hook checks if a click occurs outside all these elements.
   * @param {() => void} props.onClose - A callback function that is called when a click occurs outside the referenced elements.
   *
   */
  useDismissDropdown({
    refs: Array.from(dropdownRefs.current.values()).map((el) => ({
      current: el,
    })),
    onClose: () => setActiveDropdown(false),
  });

  /**
   * Toggles the active state of a dropdown menu.
   *
   * - If the dropdown is currently open (`true`), it will be closed (`false`).
   * - If the dropdown is currently closed (`false`), it will be opened (`true`).
   *
   * This function uses the previous state value to ensure reliable toggling.
   *
   * @function toggleDropdown
   * @returns {void} Updates the dropdown state by inverting the current value.
   */

  const toggleDropdown = (): void => {
    setActiveDropdown(true);
  };

  /**
   * Handles the file selection and upload process for updating the store logo.
   *
   * Workflow:
   * 1. Extracts the selected file from the file input event.
   * 2. Validates the file size and format (.jpg, .png only).
   * 3. If valid, uploads the file to the server via an API endpoint.
   * 4. On success:
   *    - Updates the store logo state.
   *    - Persists the updated logo URL in localStorage under `butcherDetails`.
   *    - Shows a success toast message.
   * 5. On failure:
   *    - Shows the appropriate error toast message.
   *
   * @async
   * @function handleFileChange
   * @param {React.ChangeEvent<HTMLInputElement>} event - The file input change event containing the selected file(s).
   *
   * @returns {Promise<void>} Resolves when the upload and storage update process completes.
   *
   * @throws {Error} Displays a toast error if:
   * - No file is selected.
   * - File size exceeds `fileUploadConfig.MAX_FILE_SIZE`.
   * - File format is not `.jpg` or `.png`.
   * - The API upload request fails.
   */
  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    try {
      const selectedFile = event.target.files?.[0];

      if (selectedFile) {
        if (selectedFile.size > fileUploadConfig.MAX_FILE_SIZE) {
          showToastMessage(errorMessage.MAX_FILE_SIZE_ERROR, 'error');
        } else if (!/\.(jpg|png)$/i.test(selectedFile.name)) {
          showToastMessage(errorMessage.FILE_FORMATS, 'error');
        } else {
          const response = await Axios.post(
            ApiEndpoints.CREATE_LOGO,
            { logo: selectedFile },
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            },
          );
          if (response.data.status === 'success') {
            setStoreLogo(response.data.data.logoImageUrl);

            // Updating the store logo in the local storage
            const butcherDetails = localStorage.getItem('butcherDetails');

            let details = butcherDetails ? JSON.parse(butcherDetails) : {};
            details = {
              ...details,
              profileImage: {
                ...(details.profileImage || {}),
                logoLocation: response.data.data.logoImageUrl,
              },
            };
            // Update the butcherDetails in local storage
            CustomerOrderService.setButcherDetails(details);
            showToastMessage(responseTexts.UPLOAD_SUCCESS, 'success');
          } else {
            showToastMessage(errorMessage.UPLOAD_FAILED, 'error');
          }
        }
      }
    } catch {
      showToastMessage(errorMessage.FILE_SELECTION_ERROR, 'error');
    }
  };

  return (
    <>
      <div className="flex flex-col rounded-lg bg-white shadow-[0_1px_2px_0_#C5C9E2] p-4 gap-4 duration-300 ease-out">
        <div className="flex justify-between items-center">
          <h2 className="font-medium text-xl text-[#000A16]">Store Details</h2>
          {!isStoreDetailsEditing && (
            <button
              type="button"
              onClick={toggleStoreDetailsEditing}
              className="flex gap-2 rounded-lg h-10 w-[71px] px-2 py-1"
            >
              <span className="h-4 w-4 text-[#004834] mt-1">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                >
                  <path
                    d="M12.3125 1.625C13.0938 0.84375 14.375 0.84375 15.1562 1.625L16.375 2.84375C17.1562 3.625 17.1562 4.90625 16.375 5.6875L14.875 7.1875L10.8125 3.125L12.3125 1.625ZM14.1562 7.90625L6.875 15.1875C6.5625 15.5 6.15625 15.75 5.71875 15.875L1.9375 16.9688C1.6875 17.0625 1.40625 17 1.21875 16.7812C1 16.5938 0.9375 16.3125 1 16.0625L2.125 12.2812C2.25 11.8438 2.5 11.4375 2.8125 11.125L10.0938 3.84375L14.1562 7.90625Z"
                    fill="#004834"
                  />
                </svg>
              </span>
              <span className="text-[#004834] font-bold text-base">Edit</span>
            </button>
          )}
        </div>
        {/* Store Name & Photo */}
        <div className="flex gap-4">
          <div className="flex flex-col gap-2">
            <h2 className="font-medium text-lg align-middle text-[#999DA2]">
              Store Logo
            </h2>
            <div className="relative">
              <img
                src={storeLogo}
                alt="store logo"
                className="h-[100px] w-[100px] border border-[#00000040] rounded-full"
              />
              <button
                type="button"
                onClick={(e) => {
                  toggleDropdown();
                  e.stopPropagation();
                }}
                className="absolute bottom-0 right-4 bg-white text-white rounded-full p-2 border border-[#0000001F] h-7 w-7"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4.54688 1.97949H7.42969C7.92188 1.97949 8.34375 2.30762 8.48438 2.75293L8.74219 3.47949H10.5C11.3203 3.47949 12 4.15918 12 4.97949V10.9795C12 11.8232 11.3203 12.4795 10.5 12.4795H1.5C0.65625 12.4795 0 11.8232 0 10.9795V4.97949C0 4.15918 0.65625 3.47949 1.5 3.47949H3.23438L3.49219 2.75293C3.63281 2.30762 4.05469 1.97949 4.54688 1.97949ZM6 10.2295C7.24219 10.2295 8.25 9.22168 8.25 7.97949C8.25 6.7373 7.24219 5.72949 6 5.72949C4.73438 5.72949 3.75 6.7373 3.75 7.97949C3.75 9.22168 4.73438 10.2295 6 10.2295Z"
                    fill="#000A16"
                    fillOpacity="0.6"
                  />
                </svg>
              </button>
              {activeDropdown && (
                <div
                  className="absolute left-0 mt-2 w-48 bg-white border rounded-lg shadow-lg z-10"
                  ref={(el) => {
                    if (el) {
                      dropdownRefs.current.set(1, el);
                    }
                  }}
                >
                  <ul className="py-2 text-left">
                    <li className="mx-2 my-0">
                      <input
                        type="file"
                        id="file"
                        className="hidden"
                        accept=".jpg,.png"
                        onChange={handleFileChange}
                      />
                      <button
                        type="button"
                        onClick={() => document.getElementById('file')?.click()}
                        className="w-full px-4 py-2 text-sm font-semibold hover:bg-gray-100 cursor-pointer rounded-md text-left"
                      >
                        Change Logo
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col justify-center rounded-tr rounded-br py-2 gap-1 font-medium text-lg align-middle">
            <h2 className=" text-[#656565]">Store Name*</h2>
            <p className="text-[#000A16]">{storeDetails.companyName || '-'}</p>
          </div>
        </div>
        <div className="border-b border-[#0000001F]"></div>
        {/* Store Details Display/Edit */}
        <div className="flex flex-col gap-6">
          {!isStoreDetailsEditing ? (
            // Display Mode
            <>
              <div className="flex gap-10">
                <div className="flex flex-col gap-0.5 w-full">
                  <h2 className="text-lg font-normal text-[#000A1666]">
                    Store Name*
                  </h2>
                  <p className="text-[#000A16] text-lg font-bold">
                    {storeDetails.companyName || '-'}
                  </p>
                </div>
                <div className="flex flex-col gap-0.5 w-full">
                  <h2 className="text-lg font-normal text-[#000A1666]">
                    Contact Name*
                  </h2>
                  <p className="text-[#000A16] text-lg font-bold">
                    {storeDetails.contactName || 'John Doe'}
                  </p>
                </div>
              </div>
              <div className="flex gap-10">
                <div className="flex flex-col gap-0.5 w-full">
                  <h2 className="text-lg font-normal text-[#000A1666]">
                    Address line 1
                  </h2>
                  <p className="text-[#000A16] text-lg font-bold">
                    {storeAddress.address1 || '-'}
                  </p>
                </div>
                <div className="flex flex-col gap-0.5 w-full">
                  <h2 className="text-lg font-normal text-[#000A1666]">
                    Address line 2
                  </h2>
                  <p className="text-[#000A16] text-lg font-bold">
                    {storeAddress.address2 || '-'}
                  </p>
                </div>
              </div>
              <div className="flex gap-10">
                <div className="flex flex-col gap-0.5 w-full">
                  <h2 className="text-lg font-normal text-[#000A1666]">
                    Address line 3
                  </h2>
                  <p className="text-[#000A16] text-lg font-bold">
                    {storeAddress.address3 || '-'}
                  </p>
                </div>
                <div className="flex flex-col gap-0.5 w-full">
                  <h2 className="text-lg font-normal text-[#000A1666]">City</h2>
                  <p className="text-[#000A16] text-lg font-bold">
                    {/* {getDisplayValues().store.cityName} */}
                    {storeAddress.city.cityName || '-'}
                  </p>
                </div>
              </div>
              <div className="flex gap-10">
                <div className="flex flex-col gap-0.5 w-full">
                  <h2 className="text-lg font-normal text-[#000A1666]">
                    State
                  </h2>
                  <p className="text-[#000A16] text-lg font-bold">
                    {storeAddress.state.stateName || '-'}
                  </p>
                </div>
                <div className="flex flex-col gap-0.5 w-full">
                  <h2 className="text-lg font-normal text-[#000A1666]">
                    ZipCode
                  </h2>
                  <p className="text-[#000A16] text-lg font-bold">
                    {storeAddress.postalCode.postalCode || '-'}
                  </p>
                </div>
              </div>
            </>
          ) : (
            // Edit Mode
            <>
              <div className="flex gap-10">
                <div className="flex flex-col gap-0.5 w-full">
                  <label
                    htmlFor="address1"
                    className="text-lg font-normal text-[#000A1666]"
                  >
                    Address line 1
                  </label>
                  <input
                    name="address1"
                    value={storeAddress.address1}
                    onChange={handleStoreAddressChange}
                    className="text-[#000A16] h-[41px] rounded-md border border-[#00000080] p-1"
                  />
                </div>
                <div className="flex flex-col gap-0.5 w-full">
                  <label
                    htmlFor="address2"
                    className="text-lg font-normal text-[#000A1666]"
                  >
                    Address line 2
                  </label>
                  <input
                    name="address2"
                    value={storeAddress.address2}
                    onChange={handleStoreAddressChange}
                    className="text-[#000A16] h-[41px] rounded-md border border-[#00000080] p-1"
                  />
                </div>
              </div>

              <div className="flex gap-10">
                <div className="flex flex-col gap-0.5 w-full">
                  <label
                    htmlFor="address3"
                    className="text-lg font-normal text-[#000A1666]"
                  >
                    Address line 3
                  </label>
                  <input
                    name="address3"
                    value={storeAddress.address3}
                    onChange={handleStoreAddressChange}
                    className="text-[#000A16] h-[41px] rounded-md border border-[#00000080] p-1"
                  />
                </div>
                <div className="flex flex-col gap-0.5 w-full">
                  <label
                    htmlFor="postalCode"
                    className="text-lg font-normal text-[#000A1666]"
                  >
                    ZipCode
                  </label>
                  <input
                    type="number"
                    name="postalCode"
                    value={storeZipValidation.value}
                    onChange={handleStoreZipChange}
                    onKeyPress={storeZipValidation.handleKeyPress}
                    maxLength={storeZipValidation.maxLength}
                    className={`text-[#000A16] h-[41px] rounded-md border p-1 ${
                      storeZipValidation.error
                        ? 'border-red-500'
                        : 'border-[#00000080]'
                    }`}
                  />
                  {storeZipValidation.error && (
                    <span className="text-red-500 text-sm mt-1">
                      {storeZipValidation.error}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-10">
                <div className="flex flex-col gap-0.5 w-full">
                  <label
                    htmlFor="city"
                    className="text-lg font-normal text-[#000A1666]"
                  >
                    City*
                  </label>
                  <input
                    type="text"
                    name="city"
                    readOnly
                    value={storeAddress.city.cityName}
                    onChange={handleStoreAddressChange}
                    className="text-[#000A16] h-[41px] rounded-md border border-[#00000080] p-1 bg-gray-100 cursor-not-allowed"
                  />
                </div>
                <div className="flex flex-col gap-0.5 w-full">
                  <label
                    htmlFor="state"
                    className="text-lg font-normal text-[#000A1666]"
                  >
                    State*
                  </label>
                  <input
                    type="text"
                    name="state"
                    readOnly
                    value={storeAddress.state.stateName}
                    onChange={handleStoreAddressChange}
                    className="text-[#000A16] h-[41px] rounded-md border border-[#00000080] p-1 bg-gray-100 cursor-not-allowed"
                  />
                </div>
              </div>
            </>
          )}
        </div>
        <div className="border-b border-[#0000001F]"></div>
        {/* Store Contact */}
        <div className="flex flex-col gap-2">
          <h2 className="gap-2 py-1 text-xl font-medium text-[#000A16]">
            Store Contact
          </h2>
          <div className="flex gap-6">
            <div className="flex flex-col gap-0.5 w-full">
              <h2 className="text-lg font-normal text-[#000A1666]">Email</h2>
              {!isStoreDetailsEditing ? (
                <p className="text-[#000A16] text-lg font-bold">
                  {storeDetails.storeEmail || '-'}
                </p>
              ) : (
                <input
                  name="storeEmail"
                  value={storeDetails.storeEmail}
                  readOnly
                  className="text-[#000A16] h-[41px] rounded-md border border-[#00000080] p-1 bg-gray-100 cursor-not-allowed"
                />
              )}
            </div>
            <div className="flex flex-col gap-0.5 w-full">
              <h2 className="text-lg font-normal text-[#000A1666]">
                Phone Number*
              </h2>
              {!isStoreDetailsEditing ? (
                <p className="text-[#000A16] text-lg font-bold">
                  {storeDetails.phoneNumber || '-'}
                </p>
              ) : (
                <div className="flex flex-col">
                  <input
                    name="phoneNumber"
                    value={storeDetails.phoneNumber}
                    onChange={handlePhoneChange}
                    onKeyPress={handlePhoneKeyPress}
                    maxLength={10}
                    placeholder="Enter 10-digit phone number"
                    className={`text-[#000A16] h-[41px] rounded-md border p-1 ${
                      phoneError ? 'border-red-500' : 'border-[#00000080]'
                    }`}
                  />
                  {phoneError && (
                    <span className="text-red-500 text-sm mt-1">
                      {phoneError}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="border-b border-[#0000001F]"></div>
        {/* Billing Address */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2 py-1">
            <h1 className="font-bold text-xl text-[#000A16]">
              Billing Address
            </h1>
            {isStoreDetailsEditing && (
              <div className="flex py-1 gap-2">
                <input
                  type="checkbox"
                  className="accent-[#004834] text-white"
                  checked={isBillingSameAsStore}
                  onChange={(e) => setIsBillingSameAsStore(e.target.checked)}
                />
                <p className="font-medium text-base text-[#1C1B1F]">
                  My Billing Address is same as the Store Address
                </p>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-4">
            {!isStoreDetailsEditing || isBillingSameAsStore ? (
              // Display Mode or Same as Store
              <>
                <div className="flex gap-10">
                  <div className="flex flex-col gap-0.5 w-full">
                    <h2 className="text-lg font-normal text-[#000A1666]">
                      Company Name*
                    </h2>
                    <p className="text-[#000A16] text-lg font-bold">
                      {storeDetails.companyName || '-'}
                    </p>
                  </div>
                  <div className="w-full"></div>
                </div>
                <div className="flex gap-10">
                  <div className="flex flex-col gap-0.5 w-full">
                    <h2 className="text-lg font-normal text-[#000A1666]">
                      Address line 1
                    </h2>
                    <p className="text-[#000A16] text-lg font-bold">
                      {isBillingSameAsStore
                        ? storeAddress.address1
                        : billingAddress.address1 || '-'}
                    </p>
                  </div>
                  <div className="flex flex-col gap-0.5 w-full">
                    <h2 className="text-lg font-normal text-[#000A1666]">
                      Address line 2
                    </h2>
                    <p className="text-[#000A16] text-lg font-bold">
                      {isBillingSameAsStore
                        ? storeAddress.address2
                        : billingAddress.address2 || '-'}
                    </p>
                  </div>
                </div>
                <div className="flex gap-10">
                  <div className="flex flex-col gap-0.5 w-full">
                    <h2 className="text-lg font-normal text-[#000A1666]">
                      Address line 3
                    </h2>
                    <p className="text-[#000A16] text-lg font-bold">
                      {isBillingSameAsStore
                        ? storeAddress.address3
                        : billingAddress.address3 || '-'}
                    </p>
                  </div>
                  <div className="flex flex-col gap-0.5 w-full">
                    <h2 className="text-lg font-normal text-[#000A1666]">
                      City
                    </h2>
                    <p className="text-[#000A16] text-lg font-bold">
                      {isBillingSameAsStore
                        ? storeAddress.city.cityName
                        : billingAddress.city.cityName}
                    </p>
                  </div>
                </div>
                <div className="flex gap-10">
                  <div className="flex flex-col gap-0.5 w-full">
                    <h2 className="text-lg font-normal text-[#000A1666]">
                      State
                    </h2>
                    <p className="text-[#000A16] text-lg font-bold">
                      {isBillingSameAsStore
                        ? storeAddress.state.stateName
                        : billingAddress.state.stateName}
                    </p>
                  </div>
                  <div className="flex flex-col gap-0.5 w-full">
                    <h2 className="text-lg font-normal text-[#000A1666]">
                      ZipCode
                    </h2>
                    <p className="text-[#000A16] text-lg font-bold">
                      {isBillingSameAsStore
                        ? storeAddress.postalCode.postalCode
                        : billingAddress.postalCode.postalCode}
                    </p>
                  </div>
                </div>
              </>
            ) : (
              // Edit Mode for Billing Address (when unchecked)
              <>
                <div className="flex gap-10">
                  <div className="flex flex-col gap-0.5 w-full">
                    <label
                      htmlFor="billingAddress1"
                      className="text-lg font-normal text-[#000A1666]"
                    >
                      Address line 1*
                    </label>
                    <input
                      name="address1"
                      value={billingAddress.address1}
                      onChange={handleBillingAddressChange}
                      className="text-[#000A16] h-[41px] rounded-md border border-[#00000080] p-1"
                    />
                  </div>
                  <div className="flex flex-col gap-0.5 w-full">
                    <label
                      htmlFor="billingAddress2"
                      className="text-lg font-normal text-[#000A1666]"
                    >
                      Address line 2*
                    </label>
                    <input
                      name="address2"
                      value={billingAddress.address2}
                      onChange={handleBillingAddressChange}
                      className="text-[#000A16] h-[41px] rounded-md border border-[#00000080] p-1"
                    />
                  </div>
                </div>
                <div className="flex gap-10">
                  <div className="flex flex-col gap-0.5 w-full">
                    <label
                      htmlFor="billingAddress3"
                      className="text-lg font-normal text-[#000A1666]"
                    >
                      Address line 3*
                    </label>
                    <input
                      name="address3"
                      value={billingAddress.address3}
                      onChange={handleBillingAddressChange}
                      className="text-[#000A16] h-[41px] rounded-md border border-[#00000080] p-1"
                    />
                  </div>
                  <div className="flex flex-col gap-0.5 w-full">
                    <label
                      htmlFor="billingPostalCode"
                      className="text-lg font-normal text-[#000A1666]"
                    >
                      ZipCode*
                    </label>
                    <input
                      type="number"
                      name="postalCode"
                      value={billingZipValidation.value}
                      onChange={handleBillingZipChange}
                      onKeyPress={billingZipValidation.handleKeyPress}
                      maxLength={billingZipValidation.maxLength}
                      className={`text-[#000A16] h-[41px] rounded-md border p-1 ${
                        billingZipValidation.error
                          ? 'border-red-500'
                          : 'border-[#00000080]'
                      }`}
                    />
                    {billingZipValidation.error && (
                      <span className="text-red-500 text-sm mt-1">
                        {billingZipValidation.error}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-10">
                  <div className="flex flex-col gap-0.5 w-full">
                    <label
                      htmlFor="billingCity"
                      className="text-lg font-normal text-[#000A1666]"
                    >
                      City*
                    </label>
                    <input
                      type="text"
                      name="city"
                      readOnly
                      value={billingAddress.city.cityName}
                      onChange={handleBillingAddressChange}
                      className="text-[#000A16] h-[41px] rounded-md border border-[#00000080] p-1 bg-gray-100 cursor-not-allowed"
                    />
                  </div>
                  <div className="flex flex-col gap-0.5 w-full">
                    <label
                      htmlFor="billingState"
                      className="text-lg font-normal text-[#000A1666]"
                    >
                      State*
                    </label>
                    <input
                      type="text"
                      name="state"
                      readOnly
                      value={billingAddress.state.stateName}
                      onChange={handleBillingAddressChange}
                      className="text-[#000A16] h-[41px] rounded-md border border-[#00000080] p-1 bg-gray-100 cursor-not-allowed"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        {/* Save changes button */}
        {isStoreDetailsEditing && (
          <div className="flex justify-end gap-4 h-10">
            <button
              type="button"
              onClick={closeStoreDetailsEditing}
              className="rounded gap-2 h-10 w-[71px] p-2"
            >
              <span className="text-[#004834] font-bold text-base">Cancel</span>
            </button>
            <button
              type="button"
              onClick={() => handleSave('admin')}
              className="rounded py-1.5 px-6 gap-[5.69px] h-10 w-[92px] bg-[#004834]"
            >
              <span className="text-white text-lg font-semibold text-center">
                Save
              </span>
            </button>
          </div>
        )}
      </div>
      {/* Password & Security */}
      <div className="flex flex-col rounded-lg pb-4 shadow-[0_1px_2px_0_#C5C9E2]">
        <div
          className={`flex flex-col justify-between py-4 rounded-tr-2xl rounded-tl-2xl ${isPasswordEditing ? 'px-4' : 'px-6'}`}
        >
          <h2 className="text-xl font-medium text-[#000A16]">
            Password & Security
          </h2>
          {isPasswordEditing && (
            <p className="font-normal text-base text-[#000A1666]">
              Please enter your current password to change your password
            </p>
          )}{' '}
        </div>
        {/*Change password button  */}
        {!isPasswordEditing && (
          <div className="flex">
            <div className="flex gap-4 ml-px w-full">
              <div className="pr-4 pl-6 gap-3 w-full">
                <div className="flex justify-between gap-3">
                  <div className="flex gap-2">
                    <div className="flex items-center justify-center w-4 h-7">
                      <svg
                        width="16"
                        height="19"
                        viewBox="0 0 16 19"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-7"
                      >
                        <path
                          d="M2.8125 7.72949V6.04199C2.8125 3.26465 5.0625 0.979492 7.875 0.979492C10.6523 0.979492 12.9375 3.26465 12.9375 6.04199V7.72949H13.5C14.7305 7.72949 15.75 8.74902 15.75 9.97949V16.7295C15.75 17.9951 14.7305 18.9795 13.5 18.9795H2.25C0.984375 18.9795 0 17.9951 0 16.7295V9.97949C0 8.74902 0.984375 7.72949 2.25 7.72949H2.8125ZM5.0625 7.72949H10.6875V6.04199C10.6875 4.49512 9.42188 3.22949 7.875 3.22949C6.29297 3.22949 5.0625 4.49512 5.0625 6.04199V7.72949Z"
                          fill="black"
                          fillOpacity="0.5"
                        />
                      </svg>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <h2 className="text-lg font-medium text-[#000A16] align-middle">
                        Account Password
                      </h2>
                      <p className="text-[#000A1666] text-base font-normal">
                        A Secure password helps protect your account
                      </p>
                    </div>
                  </div>
                  <div className="gap-2 rounded">
                    <div className="p-2 gap-2.5">
                      <button
                        type="button"
                        onClick={togglePasswordEditing}
                        className="text-[#004834] text-base font-bold"
                      >
                        Change Password
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Password & Security Form */}
        {isPasswordEditing && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col px-4 gap-4">
              <div className="flex gap-6 items-start">
                <label
                  htmlFor="current_password"
                  className="text-lg font-normal text-[#000A1666] min-w-[220px]"
                >
                  Current Password
                </label>

                <div className="flex flex-col w-[404px]">
                  <div className="relative h-12">
                    <input
                      type={showPasswords.oldPassword ? 'text' : 'password'}
                      name="current_password"
                      value={changePasswordObject.oldPassword}
                      onChange={(e) =>
                        handlePasswordChange('oldPassword', e.target.value)
                      }
                      className={`h-12 w-full rounded-md border p-1 ${
                        passwordErrors.oldPassword
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-[#00000080] focus:border-[#004834]'
                      }`}
                    />
                    <span
                      className="absolute right-4 top-3 cursor-pointer"
                      onClick={() => togglePasswordVisibility('oldPassword')}
                    >
                      {showPasswords.oldPassword ? (
                        /* Eye open SVG */
                        <svg
                          width="21"
                          height="16"
                          viewBox="0 0 21 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10.5 2C14.3125 2 17.6875 4.25 19.375 7.5625C19.4375 7.6875 19.5 7.84375 19.5 8.03125C19.5 8.1875 19.4375 8.34375 19.375 8.46875C17.6875 11.7812 14.3125 14 10.5 14C6.65625 14 3.28125 11.7812 1.59375 8.46875C1.53125 8.34375 1.5 8.1875 1.5 8C1.5 7.84375 1.53125 7.6875 1.59375 7.5625C3.28125 4.25 6.65625 2 10.5 2ZM10.5 12.5C12.375 12.5 13.9062 11 13.9062 9.125C13.9062 7.28125 12.375 5.75 10.5 5.75C8.59375 5.75 7.0625 7.28125 7.0625 9.125C7.0625 11 8.59375 12.5 10.5 12.5ZM10.5 11C9.40625 11 8.5625 10.1562 8.5625 9.0625C8.5625 7.96875 9.40625 7.125 10.5 7.125C11.5625 7.125 12.4062 7.96875 12.4062 9.0625C12.4062 10.1562 11.5625 11 10.5 11Z"
                            fill="black"
                            fillOpacity="0.5"
                          />
                        </svg>
                      ) : (
                        /* Eye closed SVG */
                        <svg
                          width="21"
                          height="16"
                          viewBox="0 0 21 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M20.8125 14.7188C20.9062 14.8125 21 14.9688 21 15.125C21 15.25 20.9375 15.3438 20.875 15.4375L20.5625 15.8125C20.4688 15.9375 20.3438 16 20.1562 16C20.0625 16 19.9375 15.9688 19.875 15.9062L1.1875 1.28125C1.0625 1.21875 0.96875 1.0625 0.96875 0.90625C0.96875 0.78125 1.03125 0.6875 1.09375 0.59375L1.40625 0.1875C1.5 0.09375 1.625 0.03125 1.8125 0.03125C1.90625 0.03125 2.03125 0.0625 2.125 0.125L20.8125 14.7188ZM10.25 4.59375C10.5 4.53125 10.75 4.53125 11 4.5C12.875 4.5 14.4062 6 14.4688 7.875L10.25 4.59375ZM11.7188 11.4375C11.4688 11.5 11.2188 11.5 11 11.5C9.09375 11.5 7.5625 10.0312 7.5 8.15625L11.7188 11.4375ZM11 3.5C10.375 3.5 9.75 3.59375 9.15625 3.75L7.71875 2.59375C8.75 2.21875 9.84375 2 11 2C14.8125 2 18.1875 4.25 19.875 7.5625C19.9375 7.6875 20 7.84375 20 8.03125C20 8.1875 19.9375 8.34375 19.875 8.46875C19.4375 9.34375 18.875 10.125 18.2188 10.8125L17.0312 9.90625C17.5938 9.34375 18.0312 8.71875 18.4062 8C16.9062 5.21875 14.0625 3.5 11 3.5ZM11 12.5C11.5938 12.5 12.2188 12.4375 12.8125 12.2812L14.25 13.4375C13.2188 13.7812 12.125 14 11 14C7.15625 14 3.78125 11.7812 2.09375 8.46875C2.03125 8.34375 2 8.1875 2 8C2 7.84375 2.03125 7.6875 2.09375 7.5625C2.53125 6.6875 3.09375 5.90625 3.75 5.21875L4.9375 6.125C4.375 6.6875 3.9375 7.3125 3.5625 8C5.0625 10.7812 7.90625 12.5 11 12.5Z"
                            fill="black"
                            fillOpacity="0.5"
                          />
                        </svg>
                      )}
                    </span>
                  </div>

                  {passwordErrors.oldPassword && (
                    <span className="text-red-500 text-sm mt-1">
                      {passwordErrors.oldPassword}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <label
                  htmlFor="new_password"
                  className="text-lg font-normal text-[#000A1666] min-w-[220px]"
                >
                  New Password
                </label>

                <div className="flex flex-col w-[404px]">
                  <div className="relative h-12">
                    <input
                      type={showPasswords.newPassword ? 'text' : 'password'}
                      value={changePasswordObject.newPassword}
                      onChange={(e) =>
                        handlePasswordChange('newPassword', e.target.value)
                      }
                      name="new_password"
                      id="new_password"
                      className={`h-12 w-full rounded-md border p-1 ${
                        passwordErrors.newPassword
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-[#00000080] focus:border-[#004834]'
                      }`}
                    />
                    <span
                      className="absolute right-4 top-3 cursor-pointer"
                      onClick={() => togglePasswordVisibility('newPassword')}
                    >
                      {showPasswords.newPassword ? (
                        /* Eye open icon */
                        <svg
                          width="21"
                          height="16"
                          viewBox="0 0 21 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10.5 2C14.3125 2 17.6875 4.25 19.375 7.5625C19.4375 7.6875 19.5 7.84375 19.5 8.03125C19.5 8.1875 19.4375 8.34375 19.375 8.46875C17.6875 11.7812 14.3125 14 10.5 14C6.65625 14 3.28125 11.7812 1.59375 8.46875C1.53125 8.34375 1.5 8.1875 1.5 8C1.5 7.84375 1.53125 7.6875 1.59375 7.5625C3.28125 4.25 6.65625 2 10.5 2ZM10.5 12.5C12.375 12.5 13.9062 11 13.9062 9.125C13.9062 7.28125 12.375 5.75 10.5 5.75C8.59375 5.75 7.0625 7.28125 7.0625 9.125C7.0625 11 8.59375 12.5 10.5 12.5ZM10.5 11C9.40625 11 8.5625 10.1562 8.5625 9.0625C8.5625 7.96875 9.40625 7.125 10.5 7.125C11.5625 7.125 12.4062 7.96875 12.4062 9.0625C12.4062 10.1562 11.5625 11 10.5 11Z"
                            fill="black"
                            fillOpacity="0.5"
                          />
                        </svg>
                      ) : (
                        /* Eye closed icon */
                        <svg
                          width="21"
                          height="16"
                          viewBox="0 0 21 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M20.8125 14.7188C20.9062 14.8125 21 14.9688 21 15.125C21 15.25 20.9375 15.3438 20.875 15.4375L20.5625 15.8125C20.4688 15.9375 20.3438 16 20.1562 16C20.0625 16 19.9375 15.9688 19.875 15.9062L1.1875 1.28125C1.0625 1.21875 0.96875 1.0625 0.96875 0.90625C0.96875 0.78125 1.03125 0.6875 1.09375 0.59375L1.40625 0.1875C1.5 0.09375 1.625 0.03125 1.8125 0.03125C1.90625 0.03125 2.03125 0.0625 2.125 0.125L20.8125 14.7188ZM10.25 4.59375C10.5 4.53125 10.75 4.53125 11 4.5C12.875 4.5 14.4062 6 14.4688 7.875L10.25 4.59375ZM11.7188 11.4375C11.4688 11.5 11.2188 11.5 11 11.5C9.09375 11.5 7.5625 10.0312 7.5 8.15625L11.7188 11.4375ZM11 3.5C10.375 3.5 9.75 3.59375 9.15625 3.75L7.71875 2.59375C8.75 2.21875 9.84375 2 11 2C14.8125 2 18.1875 4.25 19.875 7.5625C19.9375 7.6875 20 7.84375 20 8.03125C20 8.1875 19.9375 8.34375 19.875 8.46875C19.4375 9.34375 18.875 10.125 18.2188 10.8125L17.0312 9.90625C17.5938 9.34375 18.0312 8.71875 18.4062 8C16.9062 5.21875 14.0625 3.5 11 3.5ZM11 12.5C11.5938 12.5 12.2188 12.4375 12.8125 12.2812L14.25 13.4375C13.2188 13.7812 12.125 14 11 14C7.15625 14 3.78125 11.7812 2.09375 8.46875C2.03125 8.34375 2 8.1875 2 8C2 7.84375 2.03125 7.6875 2.09375 7.5625C2.53125 6.6875 3.09375 5.90625 3.75 5.21875L4.9375 6.125C4.375 6.6875 3.9375 7.3125 3.5625 8C5.0625 10.7812 7.90625 12.5 11 12.5Z"
                            fill="black"
                            fillOpacity="0.5"
                          />
                        </svg>
                      )}
                    </span>
                  </div>

                  {passwordErrors.newPassword && (
                    <span className="text-red-500 text-sm mt-1">
                      {passwordErrors.newPassword}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <label
                  htmlFor="confirm_new_password"
                  className="text-lg font-normal text-[#000A1666] min-w-[220px]"
                >
                  Confirm New Password
                </label>

                <div className="flex flex-col w-[404px]">
                  <div className="relative h-12">
                    <input
                      type={showPasswords.confirmPassword ? 'text' : 'password'}
                      value={changePasswordObject.confirmPassword}
                      onChange={(e) =>
                        handlePasswordChange('confirmPassword', e.target.value)
                      }
                      name="confirm_new_password"
                      id="confirm_new_password"
                      className={`h-12 w-full rounded-md border p-1 ${
                        passwordErrors.confirmPassword
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-[#00000080] focus:border-[#004834]'
                      }`}
                    />
                    <span
                      className="absolute right-4 top-3 cursor-pointer"
                      onClick={() =>
                        togglePasswordVisibility('confirmPassword')
                      }
                    >
                      {showPasswords.confirmPassword ? (
                        // Eye open icon
                        <svg
                          width="21"
                          height="16"
                          viewBox="0 0 21 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10.5 2C14.3125 2 17.6875 4.25 19.375 7.5625C19.4375 7.6875 19.5 7.84375 19.5 8.03125C19.5 8.1875 19.4375 8.34375 19.375 8.46875C17.6875 11.7812 14.3125 14 10.5 14C6.65625 14 3.28125 11.7812 1.59375 8.46875C1.53125 8.34375 1.5 8.1875 1.5 8C1.5 7.84375 1.53125 7.6875 1.59375 7.5625C3.28125 4.25 6.65625 2 10.5 2ZM10.5 12.5C12.375 12.5 13.9062 11 13.9062 9.125C13.9062 7.28125 12.375 5.75 10.5 5.75C8.59375 5.75 7.0625 7.28125 7.0625 9.125C7.0625 11 8.59375 12.5 10.5 12.5ZM10.5 11C9.40625 11 8.5625 10.1562 8.5625 9.0625C8.5625 7.96875 9.40625 7.125 10.5 7.125C11.5625 7.125 12.4062 7.96875 12.4062 9.0625C12.4062 10.1562 11.5625 11 10.5 11Z"
                            fill="black"
                            fillOpacity="0.5"
                          />
                        </svg>
                      ) : (
                        // Eye closed icon
                        <svg
                          width="21"
                          height="16"
                          viewBox="0 0 21 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M20.8125 14.7188C20.9062 14.8125 21 14.9688 21 15.125C21 15.25 20.9375 15.3438 20.875 15.4375L20.5625 15.8125C20.4688 15.9375 20.3438 16 20.1562 16C20.0625 16 19.9375 15.9688 19.875 15.9062L1.1875 1.28125C1.0625 1.21875 0.96875 1.0625 0.96875 0.90625C0.96875 0.78125 1.03125 0.6875 1.09375 0.59375L1.40625 0.1875C1.5 0.09375 1.625 0.03125 1.8125 0.03125C1.90625 0.03125 2.03125 0.0625 2.125 0.125L20.8125 14.7188ZM10.25 4.59375C10.5 4.53125 10.75 4.53125 11 4.5C12.875 4.5 14.4062 6 14.4688 7.875L10.25 4.59375ZM11.7188 11.4375C11.4688 11.5 11.2188 11.5 11 11.5C9.09375 11.5 7.5625 10.0312 7.5 8.15625L11.7188 11.4375ZM11 3.5C10.375 3.5 9.75 3.59375 9.15625 3.75L7.71875 2.59375C8.75 2.21875 9.84375 2 11 2C14.8125 2 18.1875 4.25 19.875 7.5625C19.9375 7.6875 20 7.84375 20 8.03125C20 8.1875 19.9375 8.34375 19.875 8.46875C19.4375 9.34375 18.875 10.125 18.2188 10.8125L17.0312 9.90625C17.5938 9.34375 18.0312 8.71875 18.4062 8C16.9062 5.21875 14.0625 3.5 11 3.5ZM11 12.5C11.5938 12.5 12.2188 12.4375 12.8125 12.2812L14.25 13.4375C13.2188 13.7812 12.125 14 11 14C7.15625 14 3.78125 11.7812 2.09375 8.46875C2.03125 8.34375 2 8.1875 2 8C2 7.84375 2.03125 7.6875 2.09375 7.5625C2.53125 6.6875 3.09375 5.90625 3.75 5.21875L4.9375 6.125C4.375 6.6875 3.9375 7.3125 3.5625 8C5.0625 10.7812 7.90625 12.5 11 12.5Z"
                            fill="black"
                            fillOpacity="0.5"
                          />
                        </svg>
                      )}
                    </span>
                  </div>

                  {passwordErrors.confirmPassword && (
                    <span className="text-red-500 text-sm mt-1">
                      {passwordErrors.confirmPassword}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {/* Save changes button */}
            <div className="flex justify-end px-4 gap-4 h-10">
              <button
                type="button"
                className="rounded gap-2 h-10 w-[71px] p-2"
                onClick={closePasswordEditing}
              >
                <span className="text-[#004834] font-bold text-base">
                  Cancel
                </span>
              </button>
              <button
                type="button"
                onClick={() => handleSave('password')}
                className="rounded py-1.5 px-6 gap-[5.69px] h-10 w-[92px] bg-[#004834]"
              >
                <span className="text-white text-lg font-semibold text-center">
                  Save
                </span>
              </button>
            </div>
          </div>
        )}
      </div>
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <SaveChangesConfirmation
            onClose={cancelSave}
            onConfirm={() => handleSubmit()}
          />
        </div>
      )}
    </>
  );
};
